import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d, o) => d.toLocaleDateString('es-MX', o);

const periodOf = (iso, mode) => {
  const d = new Date(iso);
  if (mode === 'month') return [`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, fmt(d, { month: 'long', year: 'numeric' })];
  const mon = new Date(d); mon.setHours(0, 0, 0, 0); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  return [mon.toISOString().slice(0, 10), `${fmt(mon, { day: 'numeric', month: 'short' })} – ${fmt(sun, { day: 'numeric', month: 'short', year: 'numeric' })}`];
};

const Diff = ({ v }) => (Math.abs(v) < 0.01 ? <span className="text-emerald-600">✓</span>
  : <span className={v > 0 ? 'text-rose-600 font-semibold' : 'text-amber-600 font-semibold'}>{v > 0 ? '+' : '−'}{m(Math.abs(v))}</span>);

const EmployeeBlock = ({ emp, pays, mode }) => {
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => {
    const g = {};
    pays.forEach(p => {
      const [k, label] = periodOf(p.created_at, mode);
      const x = g[k] || (g[k] = { k, label, paid: 0, expected: 0, count: 0, items: [] });
      x.paid += p.amount; x.expected += p.assigned_salary ?? emp.salary; x.count += 1; x.items.push(p);
    });
    return Object.values(g).sort((a, b) => b.k.localeCompare(a.k));
  }, [pays, mode, emp.salary]);
  const paid = pays.reduce((s, p) => s + p.amount, 0);
  const expected = pays.reduce((s, p) => s + (p.assigned_salary ?? emp.salary), 0);
  return (
    <div className="border rounded-lg bg-white" data-testid={`payroll-emp-${emp.id}`}>
      <button className="w-full flex flex-wrap items-center gap-4 p-3 text-left hover:bg-slate-50" onClick={() => setOpen(!open)}>
        <span className="font-semibold flex-1">{open ? '▾' : '▸'} {emp.name}{!emp.active && <span className="ml-2 text-[10px] bg-slate-200 rounded px-1">inactivo</span>}</span>
        <span className="text-xs text-slate-500">{emp.legacy ? 'Sin salario asignado' : `Salario: ${m(emp.salary)}`}</span>
        <span className="text-xs text-slate-500">{pays.length} pagos</span>
        <span className="font-bold">{m(paid)}</span>
        <span className="text-sm w-28 text-right">{emp.legacy ? '—' : <Diff v={paid - expected} />}</span>
      </button>
      {open && (
        <table className="w-full text-sm border-t">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
            <th className="px-3 py-2 text-left">{mode === 'week' ? 'Semana' : 'Mes'}</th><th className="px-3 py-2 text-right">Pagos</th>
            <th className="px-3 py-2 text-right">Esperado</th><th className="px-3 py-2 text-right">Pagado</th><th className="px-3 py-2 text-right">Diferencia</th></tr></thead>
          <tbody className="divide-y">{groups.map(g => (
            <tr key={g.k} data-testid={`payroll-period-${emp.id}-${g.k}`}>
              <td className="px-3 py-2 capitalize">{g.label}
                <div className="text-[11px] text-slate-500 normal-case">{g.items.map(p => `${fmt(new Date(p.created_at), { day: 'numeric', month: 'short' })} ${m(p.amount)}${p.description ? ` "${p.description}"` : ''} (${p.created_by_name || ''})`).join(' · ')}</div>
              </td>
              <td className="px-3 py-2 text-right">{g.count}</td><td className="px-3 py-2 text-right">{emp.legacy ? '—' : m(g.expected)}</td>
              <td className="px-3 py-2 text-right font-semibold">{m(g.paid)}</td><td className="px-3 py-2 text-right">{emp.legacy ? '—' : <Diff v={g.paid - g.expected} />}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
};

export const PayrollReport = ({ qs }) => {
  const [d, setD] = useState(null);
  const [mode, setMode] = useState('week');
  useEffect(() => {
    setD(null);
    axios.get(`${API}/reports/payroll?${qs}`, { withCredentials: true }).then(r => setD(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Error al cargar nómina'));
  }, [qs]);

  if (!d) return <p className="text-sm text-muted-foreground py-8 text-center">Cargando…</p>;
  const byEmp = {};
  d.payments.forEach(p => { (byEmp[p.employee_id || p.employee_name || 'sin'] ||= []).push(p); });
  const known = new Set(d.employees.map(e => e.id));
  const rows = [...d.employees.filter(e => e.active || byEmp[e.id]),
    ...Object.keys(byEmp).filter(k => !known.has(k)).map(k => ({ id: k, name: byEmp[k][0].employee_name || 'Sin empleado (registro anterior)', salary: 0, active: false, legacy: true }))];
  const total = d.payments.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="space-y-3" data-testid="payroll-report">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold flex-1">Nómina · {d.branch_name}</h2>
        {[['week', 'Por semana'], ['month', 'Por mes']].map(([v, l]) => (
          <button key={v} onClick={() => setMode(v)} data-testid={`payroll-mode-${v}`}
            className={`px-3 py-1 rounded-md text-xs font-semibold ${mode === v ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{l}</button>
        ))}
        <span className="font-bold" data-testid="payroll-total">Total pagado: {m(total)}</span>
      </div>
      {rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">Sin empleados ni pagos de salario en este periodo</p>
        : rows.map(e => <EmployeeBlock key={e.id} emp={e} pays={byEmp[e.id] || []} mode={mode} />)}
      <p className="text-[11px] text-slate-500">Esperado = salario asignado × número de pagos. Diferencia en rojo = se pagó de más; en amarillo = se pagó de menos. Da clic en un empleado para ver sus semanas o meses.</p>
    </div>
  );
};
